import { z } from "zod";
import {
	AGENT_IDS,
	COMMS_CHANNELS,
	CONNECTORS,
	type Integration,
} from "./nav.js";

const STORAGE_KEY = "mairko-matrix-desk";

const DeskStateSchema = z.object({
	/** Master ChatGPT-style chat vs catalog desk (Agents / Connectors / Channels). */
	workspace: z.enum(["chat", "desk"]).default("chat"),
	nav: z.enum(["agents", "connectors", "channels", "sites"]),
	agentsSurface: z.enum(["hub", "chat"]),
	agentId: z.enum(AGENT_IDS),
	agentsView: z.enum(["grid", "list"]).default("grid"),
	agentsFilter: z.enum(["all", "ready", "soon"]).default("all"),
	connectorsView: z.enum(["grid", "list"]).default("grid"),
	connectorsFilter: z
		.enum(["all", "available", "soon", "connected"])
		.default("all"),
	channelsView: z.enum(["grid", "list"]).default("grid"),
	channelsFilter: z
		.enum(["all", "available", "soon", "connected"])
		.default("all"),
	settingsOpen: z.boolean(),
	settingsTab: z.enum([
		"account",
		"workspace",
		"members",
		"notifications",
		"security",
		"billing",
	]),
	selectedIntegrationId: z.string().nullable(),
});

export type DeskState = z.infer<typeof DeskStateSchema>;
export type WorkspaceMode = DeskState["workspace"];

const DEFAULT_DESK: DeskState = {
	workspace: "chat",
	nav: "agents",
	agentsSurface: "hub",
	agentId: "onboarding",
	agentsView: "grid",
	agentsFilter: "all",
	connectorsView: "grid",
	connectorsFilter: "all",
	channelsView: "grid",
	channelsFilter: "all",
	settingsOpen: false,
	settingsTab: "account",
	selectedIntegrationId: null,
};

export function readDeskState(): DeskState {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return { ...DEFAULT_DESK };
		const parsed = DeskStateSchema.safeParse(JSON.parse(raw) as unknown);
		if (!parsed.success) return { ...DEFAULT_DESK };
		return parsed.data;
	} catch {
		return { ...DEFAULT_DESK };
	}
}

export function writeDeskState(state: DeskState): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		/* ignore quota / private mode */
	}
}

export function resolveIntegration(id: string | null): Integration | null {
	if (!id) return null;
	return (
		CONNECTORS.find((c) => c.id === id) ??
		COMMS_CHANNELS.find((c) => c.id === id) ??
		null
	);
}
